// NEC-SE-DS (Peligro sísmico, diseño sismo resistente; MIDUVI, diciembre de 2014), §10.2, Tabla 19
// "Poblaciones ecuatorianas y valor del factor Z" (págs. 90 a 108 del documento; 95 a 114 del PDF) y Tabla 1
// (Z por zona, pág. 27). Transcripción y método: docs/normativa/plan_emergencias/NEC-SE-DS_Tabla19.md.
// PDF: https://www.mit.gob.ec/wp-content/uploads/downloads/2026/03/2.-NEC-SE-DS-Peligro-Sismico-Diseno-sismorresistente.pdf
// (el sitio del MIDUVI, habitatyvivienda.gob.ec, ya no la sirve). Edición de diciembre de 2014, vigente.
// 512 poblaciones: esa copia repite la pág. 92 (págs. 97 y 98 del PDF) y la tabla repite 3 filas; se quitaron.
// SHA-256 469150d3d8c445029bd835b3487badaae90973744876d23115d662ecbaa4e5be.
// codigoInec: cantón del INEC 2026 unido por nombre normalizado (null: no se pudo unir; ver la transcripción).
// Archivo generado; no editar a mano.

import type { ZonaSismica } from './pe-amenazas';
import type { Traza } from './pe-tipos';

export interface PoblacionNEC {
  poblacion: string; parroquia: string; canton: string; provincia: string; z: number; zona: ZonaSismica; pagina: number;
  codigoInec: string | null;
}

// Tabla 1 (pág. 27): I 0,15; II 0,25; III 0,30; IV 0,35; V 0,40; VI ≥ 0,50.
export const Z_POR_ZONA: Record<ZonaSismica, number> = { I: 0.15, II: 0.25, III: 0.3, IV: 0.35, V: 0.4, VI: 0.5 };

export function zonaPorZ(z: number): ZonaSismica {
  if (z >= 0.5) return 'VI';
  const zona = (Object.keys(Z_POR_ZONA) as ZonaSismica[]).find(k => Math.abs(Z_POR_ZONA[k] - z) < 1e-9);
  if (!zona) throw new Error(`Z = ${z} no está en la Tabla 1 de la NEC-SE-DS`);
  return zona;
}

export const FUENTE_TABLA19 = 'NEC-SE-DS (MIDUVI, 2015), §10.2, Tabla 19'; // interno: docs/normativa/plan_emergencias/NEC-SE-DS_Tabla19.md

// [población, parroquia, cantón, provincia, Z, página del documento, código INEC]
const DATOS: [string, string, string, string, number, number, string | null][] = [
  ['CUENCA', 'CUENCA', 'CUENCA', 'AZUAY', 0.25, 90, '0101'],
  ['CHORDELEG', 'CHORDELEG', 'CHORDELEG', 'AZUAY', 0.25, 90, '0111'],
  ['GUALACEO', 'GUALACEO', 'GUALACEO', 'AZUAY', 0.25, 90, '0103'],
  ['SEVILLA DE ORO', 'SEVILLA DE ORO', 'SEVILLA DE ORO', 'AZUAY', 0.25, 90, '0113'],
  ['EL PAN', 'EL PAN', 'EL PAN', 'AZUAY', 0.25, 90, '0112'],
  ['PAUTE', 'CHICAN (GUILLERMO ORTEGA)', 'PAUTE', 'AZUAY', 0.25, 90, '0105'],
  ['GUARANDA', 'GUARANDA', 'GUARANDA', 'BOLIVAR', 0.35, 90, '0201'],
  ['SAN LORENZO', 'SAN LORENZO', 'GUARANDA', 'BOLIVAR', 0.35, 90, '0201'],
  ['SANTIAGO', 'SANTIAGO', 'SAN MIGUEL', 'BOLIVAR', 0.35, 90, '0205'],
  ['SAN JOSE DEL TAMBO', 'SAN JOSE DEL TAMBO', 'CHILLANES', 'BOLIVAR', 0.35, 90, '0202'],
  ['SAN SIMON', 'SAN SIMON (YACOTO)', 'GUARANDA', 'BOLIVAR', 0.35, 90, '0201'],
  ['SAN MIGUEL DE BOLIVAR', 'SAN MIGUEL', 'SAN MIGUEL', 'BOLIVAR', 0.35, 90, '0205'],
  ['SAN JOSE DE CHIMBO', 'SAN JOSE DE CHIMBO', 'CHIMBO', 'BOLIVAR', 0.35, 90, '0203'],
  ['SIMIATUG', 'SIMIATUG', 'GUARANDA', 'BOLIVAR', 0.30, 90, '0201'],
  ['SAN LUIS DE PAMBIL', 'SAN LUIS DE PAMBIL', 'GUARANDA', 'BOLIVAR', 0.30, 90, '0201'],
  ['FACUNDO VELA', 'FACUNDO VELA', 'GUARANDA', 'BOLIVAR', 0.30, 90, '0201'],
  ['LAS MERCEDES', 'LAS NAVES', 'LAS NAVES', 'BOLIVAR', 0.30, 90, '0207'],
  ['LAS NAVES', 'LAS NAVES', 'LAS NAVES', 'BOLIVAR', 0.30, 90, '0207'],
  ['SAN LUIS DE LAS MERCEDES', 'SAN LUIS DE PAMBIL', 'GUARANDA', 'BOLIVAR', 0.30, 90, '0201'],
  ['ECHEANDIA', 'ECHEANDIA', 'ECHEANDIA', 'BOLIVAR', 0.30, 90, '0204'],
  ['CHILLANES', 'CHILLANES', 'CHILLANES', 'BOLIVAR', 0.40, 90, '0202'],
  ['CAÑAR', 'CAÑAR', 'CAÑAR', 'CAÑAR', 0.30, 91, '0303'],
  ['EL TAMBO', 'EL TAMBO', 'EL TAMBO', 'CAÑAR', 0.30, 91, '0305'],
  ['LA PUNTILLA', 'PANCHO NEGRO', 'LA TRONCAL', 'CAÑAR', 0.40, 91, '0304'],
  ['LA TRONCAL', 'LA TRONCAL', 'LA TRONCAL', 'CAÑAR', 0.40, 91, '0304'],
  ['DELEG', 'DELEG', 'DELEG', 'CAÑAR', 0.25, 91, '0306'],
  ['AZOGUES', 'AZOGUES', 'AZOGUES', 'CAÑAR', 0.25, 91, '0301'],
  ['BIBLIAN', 'BIBLIAN', 'BIBLIAN', 'CAÑAR', 0.25, 91, '0302'],
  ['HUACA', 'HUACA', 'SAN PEDRO DE HUACA', 'CARCHI', 0.40, 91, '0406'],
  ['EL PLAYON DE SAN FRANCISCO', 'EL CARMELO (EL PUN)', 'TULCAN', 'CARCHI', 0.40, 91, '0401'],
  ['MARISCAL SUCRE', 'MARISCAL SUCRE', 'SAN PEDRO DE HUACA', 'CARCHI', 0.40, 91, '0406'],
  ['TULCAN', 'TULCAN', 'TULCAN', 'CARCHI', 0.40, 91, '0401'],
  ['EL ANGEL', 'EL ANGEL', 'ESPEJO', 'CARCHI', 0.40, 91, '0403'],
  ['SAN GABRIEL', 'SAN GABRIEL', 'MONTUFAR', 'CARCHI', 0.40, 91, '0405'],
  ['EL CARMELO', 'EL CARMELO (EL PUN)', 'TULCAN', 'CARCHI', 0.40, 91, '0401'],
  ['LA LIBERTAD', 'LA LIBERTAD (ALIZO)', 'ESPEJO', 'CARCHI', 0.40, 91, '0403'],
  ['JULIO ANDRADE', 'JULIO ANDRADE (OREJUELA)', 'TULCAN', 'CARCHI', 0.40, 91, '0401'],
  ['LA PAZ', 'LA PAZ', 'MONTUFAR', 'CARCHI', 0.40, 91, '0405'],
  ['SAN ISIDRO', 'SAN ISIDRO', 'ESPEJO', 'CARCHI', 0.40, 91, '0403'],
  ['SAN JOSE DE TINAJILLAS', 'GARCIA MORENO', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['SANTA MARTHA DE CUBA', 'SANTA MARTHA DE CUBA', 'TULCAN', 'CARCHI', 0.40, 91, '0401'],
  ['GARCIA MORENO', 'GARCIA MORENO', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['MONTE OLIVO', 'MONTE OLIVO', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['PIQUIUCHO', 'LOS ANDES', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['PUEBLO NUEVO', 'SAN RAFAEL', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['PUSIR', 'SAN VICENTE DE PUSIR', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['LOS ANDES', 'LOS ANDES', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['FERNANDEZ SALVADOR', 'FERNANDEZ SALVADOR', 'MONTUFAR', 'CARCHI', 0.40, 91, '0405'],
  ['SAN VICENTE DE PUSIR', 'SAN VICENTE DE PUSIR', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['MIRA', 'MIRA (CHONTAHUASI)', 'MIRA', 'CARCHI', 0.40, 91, '0404'],
  ['BOLIVAR', 'BOLIVAR', 'BOLIVAR', 'CARCHI', 0.40, 91, '0402'],
  ['TUFIÑO', 'TUFIÑO', 'TULCAN', 'CARCHI', 0.40, 92, '0401'],
  ['CALPÍ', 'CAÑI', 'COLTA', 'CHIMBORAZO', 0.35, 92, '0603'],
  ['PALLATANGA', 'PALLATANGA', 'PALLATANGA', 'CHIMBORAZO', 0.40, 92, '0608'],
  ['PUNGAL GRANDE', 'GUANO', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['GUANO', 'GUANO', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['PENIPE', 'PENIPE', 'PENIPE', 'CHIMBORAZO', 0.40, 92, '0609'],
  ['SAN ANDRES', 'SAN ANDRES', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['SAN ISIDRO DE PATULU', 'SAN ISIDRO DE PATULU', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['VALPARAISO', 'VALPARAISO', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['SAN JOSE DE CHAZO', 'SAN JOSE DEL CHAZO', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['GUANANDO', 'GUANANDO', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['CALPI', 'CALPI', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['LICÍN', 'RIOBAMBA', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['QUIMIAG', 'QUIMIAG', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['ASUNCION', 'CALPI', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['MACAJÁ', 'RIOBAMBA', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['PUELA', 'PUELA', 'PENIPE', 'CHIMBORAZO', 0.40, 92, '0609'],
  ['RIOBAMBA', 'GUANO', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['ILAPO', 'ILAPO', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['SAN GERARDO', 'SAN GERARDO DE PACAICAGUAN', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['SAN ANTONIO DE BAYUSHIG', 'SAN ANTONIO DE BAYUSHIG', 'PENIPE', 'CHIMBORAZO', 0.40, 92, '0609'],
  ['SANTA FE DE GALAN', 'SANTA FE DE GALAN', 'GUANO', 'CHIMBORAZO', 0.40, 92, '0607'],
  ['SAN VICENTE', 'RIOBAMBA', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['CHAMBO', 'CHAMBO', 'CHAMBO', 'CHIMBORAZO', 0.40, 92, '0604'],
  ['PUNIN', 'PUNIN', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['LICTO', 'LICTO', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['PUNGALA', 'PUNGALA', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['YARUQUIES', 'RIOBAMBA', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['SAN LUIS', 'SAN LUIS', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 92, '0601'],
  ['JUAN DE VELASCO', 'JUAN DE VELASCO (PANGOR)', 'COLTA', 'CHIMBORAZO', 0.40, 92, '0603'],
  ['CEBADAS', 'CEBADAS', 'GUAMOTE', 'CHIMBORAZO', 0.40, 93, '0606'],
  ['CAJABAMBA', 'VILLA LA UNION (CAJABAMBA)', 'COLTA', 'CHIMBORAZO', 0.40, 93, '0603'],
  ['CUBIJIES', 'GUANO', 'GUANO', 'CHIMBORAZO', 0.40, 93, '0607'],
  ['SAN JUAN', 'SAN JUAN', 'RIOBAMBA', 'CHIMBORAZO', 0.40, 93, '0601'],
  ['COLUMBE', 'COLUMBE', 'COLTA', 'CHIMBORAZO', 0.40, 93, '0603'],
  ['MATUS', 'EL ALTAR', 'PENIPE', 'CHIMBORAZO', 0.40, 93, '0609'],
  ['VILLA LA UNION', 'VILLA LA UNION (CAJABAMBA)', 'COLTA', 'CHIMBORAZO', 0.40, 93, '0603'],
  ['GUAMOTE', 'GUAMOTE', 'GUAMOTE', 'CHIMBORAZO', 0.40, 93, '0606'],
  ['LLACTAPAMBA DE ALAO', 'PUNGALA', 'RIOBAMBA', 'CHIMBORAZO', 0.35, 93, '0601'],
  ['CHUNCHI', 'CHUNCHI', 'CHUNCHI', 'CHIMBORAZO', 0.35, 93, '0605'],
  ['ALAUSI', 'SIBAMBE', 'ALAUSI', 'CHIMBORAZO', 0.35, 93, '0602'],
  ['ANGAMARCA', 'ANGAMARCA', 'PUJILI', 'COTOPAXI', 0.30, 93, '0504'],
  ['LA MANA', 'LA MANA', 'LA MANA', 'COTOPAXI', 0.30, 93, '0502'],
  ['ZUMBAHUA', 'ZUMBAHUA', 'PUJILI', 'COTOPAXI', 0.30, 93, '0504'],
  ['EL CORAZON', 'EL CORAZON', 'PANGUA', 'COTOPAXI', 0.30, 93, '0503'],
  ['MORASPUNGO', 'MORASPUNGO', 'PANGUA', 'COTOPAXI', 0.30, 93, '0503'],
  ['SIGCHOS', 'SIGCHOS', 'SIGCHOS', 'COTOPAXI', 0.30, 93, '0507'],
  ['CUSUBAMBA', 'CUSUBAMBA', 'SALCEDO', 'COTOPAXI', 0.40, 93, '0505'],
  ['ALAQUES', 'ALAQUES (ALAQUEZ)', 'LATACUNGA', 'COTOPAXI', 0.40, 93, '0501'],
  ['MULALILLO', 'MULALILLO', 'SALCEDO', 'COTOPAXI', 0.40, 93, '0505'],
  ['PANSALEO', 'PANSALEO', 'SALCEDO', 'COTOPAXI', 0.40, 93, '0505'],
  ['ANTONIO JOSE HOLGUIN', 'ANTONIO JOSE HOLGUIN', 'SALCEDO', 'COTOPAXI', 0.40, 93, '0505'],
  ['GUAITACAMA', 'GUAITACAMA (GUAYTACAMA)', 'LATACUNGA', 'COTOPAXI', 0.40, 93, '0501'],
  ['MULLIQUINDIL', 'MULLIQUINDIL (SANTA ANA)', 'SALCEDO', 'COTOPAXI', 0.40, 93, '0505'],
  ['SAQUISILI', 'CHANTILIN', 'SAQUISILI', 'COTOPAXI', 0.40, 93, '0506'],
  ['MARISCAL SUCRE', 'POALO', 'LATACUNGA', 'COTOPAXI', 0.40, 93, '0501'],
  ['LATACUNGA', 'LATACUNGA', 'LATACUNGA', 'COTOPAXI', 0.40, 93, '0501'],
  ['LA VICTORIA', 'LA VICTORIA', 'PUJILI', 'COTOPAXI', 0.40, 93, '0504'],
  ['SAN JUAN DE PASTOCALLE', 'SAN JUAN DE PASTOCALLE', 'LATACUNGA', 'COTOPAXI', 0.40, 94, '0501'],
  ['TANICUCHA', 'TANICUCHI', 'LATACUNGA', 'COTOPAXI', 0.40, 94, '0501'],
  ['TOACASO', 'TOACASO', 'LATACUNGA', 'COTOPAXI', 0.40, 94, '0501'],
  ['MULALO', 'MULALO', 'LATACUNGA', 'COTOPAXI', 0.40, 94, '0501'],
  ['SAN AGUSTIN DE CALLO', 'MULALO', 'LATACUNGA', 'COTOPAXI', 0.40, 94, '0501'],
  ['SAN MIGUEL DE SALCEDO', 'SAN MIGUEL', 'SALCEDO', 'COTOPAXI', 0.40, 94, '0505'],
  ['PUJILI', 'PUJILI', 'PUJILI', 'COTOPAXI', 0.40, 94, '0504'],
  ['PORTOVELO', 'PORTOVELO', 'PORTOVELO', 'EL ORO', 0.30, 94, '0711'],
  ['CHILLA', 'CHILLA', 'CHILLA', 'EL ORO', 0.30, 94, '0705'],
  ['PACCHA', 'PACCHA', 'ATAHUALPA', 'EL ORO', 0.30, 94, '0703'],
  ['PIÑAS', 'PIÑAS', 'PIÑAS', 'EL ORO', 0.30, 94, '0710'],
  ['ZARUMA', 'MALVAS', 'ZARUMA', 'EL ORO', 0.30, 94, '0713'],
  ['HUAQUILLAS', 'HUAQUILLAS', 'HUAQUILLAS', 'EL ORO', 0.40, 94, '0707'],
  ['SANTA ROSA', 'SANTA ROSA', 'SANTA ROSA', 'EL ORO', 0.40, 94, '0712'],
  ['ARENILLAS', 'ARENILLAS', 'ARENILLAS', 'EL ORO', 0.40, 94, '0702'],
  ['BELLAVISTA', 'BELLAVISTA', 'SANTA ROSA', 'EL ORO', 0.40, 94, '0712'],
  ['MACHALA', 'MACHALA', 'MACHALA', 'EL ORO', 0.40, 94, '0701'],
  ['BALSAS', 'BALSAS', 'BALSAS', 'EL ORO', 0.35, 94, '0704'],
  ['MARCABELI', 'MARCABELI', 'MARCABELI', 'EL ORO', 0.35, 94, '0708'],
  ['PASAJE', 'PASAJE', 'PASAJE', 'EL ORO', 0.35, 94, '0709'],
  ['BELLA MARIA', 'BELLAMARIA', 'SANTA ROSA', 'EL ORO', 0.40, 94, '0712'],
  ['EL GUABO', 'EL GUABO', 'EL GUABO', 'EL ORO', 0.40, 94, '0706'],
  ['LA UNION', 'LA UNION', 'QUININDE', 'ESMERALDAS', 0.50, 94, '0804'],
  ['VICHE', 'VICHE', 'QUININDE', 'ESMERALDAS', 0.50, 94, '0804'],
  ['MONTERREY', 'ROSA ZARATE (QUININDE)', 'QUININDE', 'ESMERALDAS', 0.50, 94, '0804'],
  ['LA VILLEGAS', 'ROSA ZARATE (QUININDE)', 'QUININDE', 'ESMERALDAS', 0.50, 94, '0804'],
  ['ATACAMES', 'ATACAMES', 'ATACAMES', 'ESMERALDAS', 0.50, 94, '0806'],
  ['ROSA ZARATE', 'ROSA ZARATE (QUININDE)', 'QUININDE', 'ESMERALDAS', 0.50, 94, '0804'],
  ['BOLIVAR', 'BOLIVAR', 'MUISNE', 'ESMERALDAS', 0.50, 94, '0803'],
  ['LA INDEPENDENCIA', 'ROSA ZARATE (QUININDE)', 'QUININDE', 'ESMERALDAS', 0.50, 94, '0804'],
  ['LA TE', 'MALIMPIA', 'QUININDE', 'ESMERALDAS', 0.50, 95, '0804'],
  ['ZAPALLO', 'MALIMPIA', 'QUININDE', 'ESMERALDAS', 0.50, 95, '0804'],
  ['ESMERALDAS', 'ESMERALDAS', 'ESMERALDAS', 'ESMERALDAS', 0.50, 95, '0801'],
  ['SAN LORENZO', 'SAN LORENZO', 'SAN LORENZO', 'ESMERALDAS', 0.50, 95, '0805'],
  ['PUERTO GAVIOTA', 'TONSUPA', 'ATACAMES', 'ESMERALDAS', 0.50, 95, '0806'],
  ['LA CONCORDIA', 'LA CONCORDIA', 'LA CONCORDIA', 'ESMERALDAS', 0.50, 95, '2302'],
  ['PLAN PILOTO', 'LA CONCORDIA', 'LA CONCORDIA', 'ESMERALDAS', 0.40, 95, '2302'],
  ['VALDEZ', 'VALDEZ (LIMONES)', 'ELOY ALFARO', 'ESMERALDAS', 0.50, 95, '0802'],
  ['MUISNE', 'MUISNE', 'MUISNE', 'ESMERALDAS', 0.50, 95, '0803'],
  ['JUAN GOMEZ RENDON', 'JUAN GOMEZ RENDON (PROGRESO)', 'GUAYAQUIL', 'GUAYAS', 0.50, 95, '0901'],
  ['GENERAL VILLAMIL', 'GENERAL VILLAMIL (PLAYAS)', 'PLAYAS', 'GUAYAS', 0.50, 95, '0921'],
  ['LA ESTACADA', 'PEDRO CARBO', 'PEDRO CARBO', 'GUAYAS', 0.50, 95, '0914'],
  ['SAN LORENZO', 'JUAN GOMEZ RENDON (PROGRESO)', 'GUAYAQUIL', 'GUAYAS', 0.50, 95, '0901'],
  ['SAN CAMILO', 'GUAYAS (PUEBLO NUEVO)', 'EMPALME', 'GUAYAS', 0.35, 95, '0908'],
  ['SAN JACINTO DE YAGUACHI', 'SAN JACINTO DE YAGUACHI', 'SAN JACINTO DE YAGUACHI', 'GUAYAS', 0.35, 95, '0920'],
  ['MARISCAL SUCRE', 'MARISCAL SUCRE (HUAQUES)', 'MILAGRO', 'GUAYAS', 0.35, 95, '0910'],
  ['LORENZO DE GARAICOA', 'CRNEL. LORENZO DE GARAICOA', 'SIMON BOLIVAR', 'GUAYAS', 0.35, 95, '0922'],
  ['SANTO DOMINGO', 'SIMON BOLIVAR', 'SIMON BOLIVAR', 'GUAYAS', 0.35, 95, '0922'],
  ['SAMBORONDON', 'SAMBORONDON', 'SAMBORONDON', 'GUAYAS', 0.35, 95, '0916'],
  ['GENERAL VERNAZA', 'GRAL. VERNAZA (DOS ESTEROS)', 'SALITRE', 'GUAYAS', 0.35, 95, '0919'],
  ['MATA DE CACAO', 'CRNEL. LORENZO DE GARAICOA', 'SIMON BOLIVAR', 'GUAYAS', 0.35, 95, '0922'],
  ['VELASCO IBARRA', 'VELASCO IBARRA (CAB. EL EMPALME)', 'EMPALME', 'GUAYAS', 0.35, 95, '0908'],
  ['MILAGRO', 'MILAGRO', 'MILAGRO', 'GUAYAS', 0.35, 95, '0910'],
  ['PEDRO J. MONTERO', 'GRAL. PEDRO J. MONTERO (BOLICHE)', 'SAN JACINTO DE YAGUACHI', 'GUAYAS', 0.35, 95, '0920'],
  ['NARANJITO', 'NARANJITO', 'NARANJITO', 'GUAYAS', 0.35, 95, '0912'],
  ['CORONEL MARCELINO MARIDUEÑA', 'CRNEL. MARCELINO MARIDUEÑA', 'CRNEL. MARCELINO MARIDUEÑA', 'GUAYAS', 0.35, 95, '0923'],
  ['ALFREDO BAQUERIZO MORENO', 'ALFREDO BAQUERIZO MORENO', 'ALFREDO BAQUERIZO MORENO', 'GUAYAS', 0.30, 96, '0902'],
  ['CARLOS J. AROSEMENA', 'GUAYAS (PUEBLO NUEVO)', 'EMPALME', 'GUAYAS', 0.40, 96, '0908'],
  ['LA GUAYAQUIL', 'BALZAR', 'BALZAR', 'GUAYAS', 0.40, 96, '0904'],
  ['PALESTINA', 'PALESTINA', 'PALESTINA', 'GUAYAS', 0.40, 96, '0913'],
  ['PEDRO VELEZ', 'EL ROSARIO', 'EMPALME', 'GUAYAS', 0.40, 96, '0908'],
  ['SANTA LUCIA', 'EL ROSARIO', 'EMPALME', 'GUAYAS', 0.40, 96, '0908'],
  ['BALZAR', 'BALZAR', 'BALZAR', 'GUAYAS', 0.40, 96, '0904'],
  ['SANTA LUCIA', 'SANTA LUCIA', 'SANTA LUCIA', 'GUAYAS', 0.40, 96, '0918'],
  ['JUNQUILLAL', 'JUNQUILLAL', 'SALITRE', 'GUAYAS', 0.40, 96, '0919'],
  ['CERRITOS', 'BALZAR', 'BALZAR', 'GUAYAS', 0.40, 96, '0904'],
  ['EL SALITRE', 'EL SALITRE (LAS RAMAS)', 'SALITRE', 'GUAYAS', 0.40, 96, '0919'],
  ['DAULE', 'DAULE', 'DAULE', 'GUAYAS', 0.40, 96, '0906'],
  ['COLIMES', 'COLIMES', 'COLIMES', 'GUAYAS', 0.40, 96, '0905'],
  ['CASCAJAL', 'VALLE DE LA VIRGEN', 'PEDRO CARBO', 'GUAYAS', 0.40, 96, '0914'],
  ['ZAMORA NUEVO', 'PEDRO CARBO', 'PEDRO CARBO', 'GUAYAS', 0.40, 96, '0914'],
  ['EL PIÑAL DE ABAJO', 'LIMONAL', 'DAULE', 'GUAYAS', 0.40, 96, '0906'],
  ['PEDRO CARBO', 'PEDRO CARBO', 'PEDRO CARBO', 'GUAYAS', 0.40, 96, '0914'],
  ['LAS CAÑAS', 'LOMAS DE SARGENTILLO', 'LOMAS DE SARGENTILLO', 'GUAYAS', 0.40, 96, '0924'],
  ['PUEBLO NUEVO', 'ISIDRO AYORA', 'ISIDRO AYORA', 'GUAYAS', 0.40, 96, '0928'],
  ['LAS MERCEDES', 'ISIDRO AYORA', 'ISIDRO AYORA', 'GUAYAS', 0.40, 96, '0928'],
  ['SABANILLA', 'SABANILLA', 'PEDRO CARBO', 'GUAYAS', 0.40, 96, '0914'],
  ['ISIDRO AYORA', 'ISIDRO AYORA', 'ISIDRO AYORA', 'GUAYAS', 0.40, 96, '0928'],
  ['LOMAS DE SARGENTILLO', 'LOMAS DE SARGENTILLO', 'LOMAS DE SARGENTILLO', 'GUAYAS', 0.40, 96, '0924'],
  ['BALAO', 'BALAO', 'BALAO', 'GUAYAS', 0.40, 96, '0903'],
  ['NARANJAL', 'NARANJAL', 'NARANJAL', 'GUAYAS', 0.40, 96, '0911'],
  ['EL TRIUNFO', 'EL TRIUNFO', 'EL TRIUNFO', 'GUAYAS', 0.40, 96, '0909'],
  ['TAURA', 'VIRGEN DE FATIMA', 'SAN JACINTO DE YAGUACHI', 'GUAYAS', 0.40, 96, '0920'],
  ['NARCIZA DE JESUS', 'NARCISA DE JESUS', 'NOBOL', 'GUAYAS', 0.40, 96, '0925'],
  ['LA PUNTILLA', 'SAMBORONDON', 'SAMBORONDON', 'GUAYAS', 0.40, 97, '0916'],
  ['LAUREL', 'JUNQUILLAL', 'SALITRE', 'GUAYAS', 0.40, 97, '0919'],
  ['LAUREL', 'LAUREL', 'DAULE', 'GUAYAS', 0.40, 97, '0906'],
  ['PUEBLO NUEVO', 'SIMON BOLIVAR', 'SIMON BOLIVAR', 'GUAYAS', 0.50, 97, '0922'],
  ['SIMON BOLIVAR', 'SIMON BOLIVAR', 'SIMON BOLIVAR', 'GUAYAS', 0.50, 97, '0922'],
  ['KILOMETRO VEINTE Y SEIS', 'VIRGEN DE FATIMA', 'SAN JACINTO DE YAGUACHI', 'GUAYAS', 0.35, 97, '0920'],
  ['ELOY ALFARO', 'ELOY ALFARO (DURAN)', 'DURAN', 'GUAYAS', 0.40, 97, '0907'],
  ['GUAYAQUIL', 'GUAYAQUIL', 'GUAYAQUIL', 'GUAYAS', 0.40, 97, '0901'],
  ['CARPUELA', 'AMBUQUI', 'IBARRA', 'IMBABURA', 0.40, 97, '1001'],
  ['CHALGUAYACU', 'PIMAMPIRO', 'PIMAMPIRO', 'IMBABURA', 0.40, 97, '1005'],
  ['PIMAMPIRO', 'PIMAMPIRO', 'PIMAMPIRO', 'IMBABURA', 0.40, 97, '1005'],
  ['MARIANO ACOSTA', 'MARIANO ACOSTA', 'PIMAMPIRO', 'IMBABURA', 0.40, 97, '1005'],
  ['EL JUNCAL', 'AMBUQUI', 'IBARRA', 'IMBABURA', 0.40, 97, '1001'],
  ['SAN RAFAEL', 'SAN RAFAEL', 'OTAVALO', 'IMBABURA', 0.40, 97, '1004'],
  ['AMBUQUI', 'AMBUQUI', 'IBARRA', 'IMBABURA', 0.40, 97, '1001'],
  ['SAN ANTONIO DE IBARRA', 'SAN ANTONIO', 'IBARRA', 'IMBABURA', 0.40, 97, '1001'],
  ['SAN FRANCISCO DE NATABUELA', 'SAN ANTONIO', 'IBARRA', 'IMBABURA', 0.40, 97, '1001'],
  ['SAN JOSE DE CHALTURA', 'SAN JOSE DE CHALTURA', 'ANTONIO ANTE', 'IMBABURA', 0.40, 97, '1002'],
  ['IMANTAG', 'IMANTAG', 'COTACACHI', 'IMBABURA', 0.40, 97, '1003'],
  ['COTACACHI', 'COTACACHI', 'COTACACHI', 'IMBABURA', 0.40, 97, '1003'],
  ['QUIROGA', 'QUIROGA', 'COTACACHI', 'IMBABURA', 0.40, 97, '1003'],
  ['SAN MIGUEL DE IBARRA', 'IMBAYA (SAN LUIS DE COBUENDO)', 'ANTONIO ANTE', 'IMBABURA', 0.40, 97, '1002'],
  ['LA CALERA', 'COTACACHI', 'COTACACHI', 'IMBABURA', 0.40, 97, '1003'],
  ['SAN ROQUE', 'SAN ROQUE', 'ANTONIO ANTE', 'IMBABURA', 0.40, 97, '1002'],
  ['SAN JUAN DE ILUMAN', 'SAN JUAN DE ILUMAN', 'OTAVALO', 'IMBABURA', 0.40, 97, '1004'],
  ['SALINAS', 'SALINAS', 'IBARRA', 'IMBABURA', 0.40, 97, '1001'],
  ['CAHUASQUI', 'CAHUASQUI', 'SAN MIGUEL DE URCUQUI', 'IMBABURA', 0.40, 97, '1006'],
  ['PABLO ARENAS', 'PABLO ARENAS', 'SAN MIGUEL DE URCUQUI', 'IMBABURA', 0.40, 97, '1006'],
  ['TUMBABIRO', 'TUMBABIRO', 'SAN MIGUEL DE URCUQUI', 'IMBABURA', 0.40, 98, '1006'],
  ['SAN BLAS', 'URCUQUI', 'SAN MIGUEL DE URCUQUI', 'IMBABURA', 0.40, 98, '1006'],
  ['PRIORATO', 'SAN MIGUEL DE IBARRA', 'IBARRA', 'IMBABURA', 0.40, 98, '1001'],
  ['SAN MIGUEL DE YAHUARCOCHA', 'SAN MIGUEL DE IBARRA', 'IBARRA', 'IMBABURA', 0.40, 98, '1001'],
  ['CARANQUI', 'SAN MIGUEL DE IBARRA', 'IBARRA', 'IMBABURA', 0.40, 98, '1001'],
  ['SANTA ROSA', 'SAN MIGUEL DE IBARRA', 'IBARRA', 'IMBABURA', 0.40, 98, '1001'],
  ['OTAVALO', 'DOCTOR MIGUEL EGAS CABEZAS', 'OTAVALO', 'IMBABURA', 0.40, 98, '1004'],
  ['CRUZ LOMA', 'EUGENIO ESPEJO (CALPAQUI)', 'OTAVALO', 'IMBABURA', 0.40, 98, '1004'],
  ['SAN JOSE DE QUICHINCHE', 'SAN JOSE DE QUICHINCHE', 'OTAVALO', 'IMBABURA', 0.40, 98, '1004'],
  ['SAN PABLO DEL LAGO', 'SAN PABLO', 'OTAVALO', 'IMBABURA', 0.40, 98, '1004'],
  ['GONZALEZ SUAREZ', 'GONZALEZ SUAREZ', 'OTAVALO', 'IMBABURA', 0.40, 98, '1004'],
  ['ATUNTAQUI', 'SAN JOSE DE CHALTURA', 'ANTONIO ANTE', 'IMBABURA', 0.40, 98, '1002'],
  ['URCUQUI', 'URCUQUI', 'SAN MIGUEL DE URCUQUI', 'IMBABURA', 0.40, 98, '1006'],
  ['CHAGUARPAMBA', 'CHAGUARPAMBA', 'CHAGUARPAMBA', 'LOJA', 0.30, 98, '1105'],
  ['CATACOCHA', 'CATACOCHA', 'PALTAS', 'LOJA', 0.30, 98, '1109'],
  ['ALAMOR', 'ALAMOR', 'PUYANGO', 'LOJA', 0.40, 98, '1110'],
  ['MACARA', 'MACARA', 'MACARA', 'LOJA', 0.35, 98, '1108'],
  ['CELICA', 'CELICA', 'CELICA', 'LOJA', 0.35, 98, '1104'],
  ['SOZORANGA', 'SOZORANGA', 'SOZORANGA', 'LOJA', 0.35, 98, '1112'],
  ['CATAMAYO', 'CATAMAYO (LA TOMA)', 'CATAMAYO', 'LOJA', 0.25, 98, '1103'],
  ['AMALUZA', 'AMALUZA', 'ESPINDOLA', 'LOJA', 0.25, 98, '1106'],
  ['QUILANGA', 'QUILANGA', 'QUILANGA', 'LOJA', 0.25, 98, '1115'],
  ['CARIAMANGA', 'CARIAMANGA', 'CALVAS', 'LOJA', 0.25, 98, '1102'],
  ['LOJA', 'LOJA', 'LOJA', 'LOJA', 0.25, 98, '1101'],
  ['SAN JACINTO DE BUENA FE', 'SAN JACINTO DE BUENA FE', 'BUENA FE', 'LOS RIOS', 0.35, 98, '1210'],
  ['JAUNECHE', 'PALENQUE', 'PALENQUE', 'LOS RIOS', 0.35, 98, '1209'],
  ['ANTONIO SOTOMAYOR', 'ANTONIO SOTOMAYOR', 'VINCES', 'LOS RIOS', 0.35, 98, '1208'],
  ['BABA', 'BABA', 'BABA', 'LOS RIOS', 0.35, 99, '1202'],
  ['QUEVEDO', 'QUEVEDO', 'QUEVEDO', 'LOS RIOS', 0.35, 99, '1205'],
  ['VINCES', 'VINCES', 'VINCES', 'LOS RIOS', 0.35, 99, '1208'],
  ['PALENQUE', 'PALENQUE', 'PALENQUE', 'LOS RIOS', 0.35, 99, '1209'],
  ['LA UNION', 'LA UNION', 'BABAHOYO', 'LOS RIOS', 0.30, 99, '1201'],
  ['COSTA AZUL', 'VALENCIA', 'VALENCIA', 'LOS RIOS', 0.30, 99, '1211'],
  ['VALENCIA', 'VALENCIA', 'VALENCIA', 'LOS RIOS', 0.30, 99, '1211'],
  ['MONTALVO', 'MONTALVO', 'MONTALVO', 'LOS RIOS', 0.30, 99, '1203'],
  ['QUINSALOMA', 'QUINSALOMA', 'QUINSALOMA', 'LOS RIOS', 0.30, 99, '1213'],
  ['SAN JUAN', 'SAN JUAN', 'PUEBLOVIEJO', 'LOS RIOS', 0.30, 99, '1204'],
  ['SAN CARLOS', 'SAN CARLOS', 'QUEVEDO', 'LOS RIOS', 0.30, 99, '1205'],
  ['MOCACHE', 'MOCACHE', 'MOCACHE', 'LOS RIOS', 0.30, 99, '1212'],
  ['PUEBLOVIEJO', 'PUEBLOVIEJO', 'PUEBLOVIEJO', 'LOS RIOS', 0.30, 99, '1204'],
  ['CATARAMA', 'CATARAMA', 'URDANETA', 'LOS RIOS', 0.30, 99, '1206'],
  ['BABAHOYO', 'BABAHOYO', 'BABAHOYO', 'LOS RIOS', 0.30, 99, '1201'],
  ['VENTANAS', 'VENTANAS', 'VENTANAS', 'LOS RIOS', 0.30, 99, '1207'],
  ['PATRICIA PILAR', 'PATRICIA PILAR', 'BUENA FE', 'LOS RIOS', 0.40, 99, '1210'],
  ['COLON', 'PORTOVIEJO', 'PORTOVIEJO', 'MANABI', 0.50, 99, '1301'],
  ['JULCUY', 'JULCUY', 'JIPIJAPA', 'MANABI', 0.50, 99, '1306'],
  ['CHARAPOTO', 'CHARAPOTO', 'SUCRE', 'MANABI', 0.50, 99, '1314'],
  ['FLAVIO ALFARO', 'FLAVIO ALFARO', 'FLAVIO ALFARO', 'MANABI', 0.50, 99, '1305'],
  ['SAN PEDRO DE SUMA', 'SAN PEDRO DE SUMA', 'EL CARMEN', 'MANABI', 0.50, 99, '1304'],
  ['ROCAFUERTE', 'ROCAFUERTE', 'ROCAFUERTE', 'MANABI', 0.50, 99, '1312'],
  ['RIOCHICO', 'RIOCHICO (RIO CHICO)', 'PORTOVIEJO', 'MANABI', 0.50, 99, '1301'],
  ['SAN ISIDRO', 'SAN ISIDRO', 'BAHIA DE CARAQUEZ', 'MANABI', 0.50, 99, null],
  ['ELOY ALFARO', 'ELOY ALFARO', 'CHONE', 'MANABI', 0.50, 99, '1303'],
  ['SAN JACINTO', 'CHARAPOTO', 'SUCRE', 'MANABI', 0.50, 99, '1314'],
  ['SAN VICENTE', 'SAN VICENTE', 'SAN VICENTE', 'MANABI', 0.50, 99, '1322'],
  ['LEONIDAS PLAZA', 'BAHIA DE CARAQUEZ', 'SUCRE', 'MANABI', 0.50, 99, '1314'],
  ['CALCETA', 'CALCETA', 'BOLIVAR', 'MANABI', 0.50, 99, '1302'],
  ['JARAMIJO', 'JARAMIJO', 'JARAMIJO', 'MANABI', 0.50, 99, '1321'],
  ['SANTA ANA DE VUELTA LARGA', 'SANTA ANA DE VUELTA LARGA', 'SANTA ANA', 'MANABI', 0.50, 100, '1313'],
  ['CHONE', 'CHONE', 'CHONE', 'MANABI', 0.50, 100, '1303'],
  ['EL AROMO', 'MANTA', 'MANTA', 'MANABI', 0.50, 100, '1308'],
  ['PILES', 'MONTECRISTI', 'MONTECRISTI', 'MANABI', 0.50, 100, '1309'],
  ['SAN LORENZO', 'SAN LORENZO', 'MANTA', 'MANABI', 0.50, 100, '1308'],
  ['PACOCHE ABAJO', 'SANTA MARIANITA', 'MANTA', 'MANABI', 0.50, 100, '1308'],
  ['EL LIMON', 'PORTOVIEJO', 'PORTOVIEJO', 'MANABI', 0.50, 100, '1301'],
  ['CALDERON', 'ABDON CALDERON (SAN FRANCISCO)', 'PORTOVIEJO', 'MANABI', 0.50, 100, '1301'],
  ['PORTOVIEJO', 'PORTOVIEJO', 'PORTOVIEJO', 'MANABI', 0.50, 100, '1301'],
  ['SAN PLACIDO', 'SAN PLACIDO', 'PORTOVIEJO', 'MANABI', 0.50, 100, '1301'],
  ['SUCRE', 'SUCRE', '24 DE MAYO', 'MANABI', 0.50, 100, '1316'],
  ['SANCAN', 'JIPIJAPA', 'JIPIJAPA', 'MANABI', 0.50, 100, '1306'],
  ['MEMBRILLAL', 'MEMBRILLAL', 'JIPIJAPA', 'MANABI', 0.50, 100, '1306'],
  ['RIO DE CAÑA', 'MONTECRISTI', 'MONTECRISTI', 'MANABI', 0.50, 100, '1309'],
  ['JIPIJAPA', 'JIPIJAPA', 'JIPIJAPA', 'MANABI', 0.50, 100, '1306'],
  ['EL ANEGADO', 'EL ANEGADO (CAB EN ELOY ALFARO)', 'JIPIJAPA', 'MANABI', 0.50, 100, '1306'],
  ['EL ESFUERZO', 'BELLAVISTA', '24 DE MAYO', 'MANABI', 0.50, 100, '1316'],
  ['NOBOA', 'NOBOA', '24 DE MAYO', 'MANABI', 0.50, 100, '1316'],
  ['BELLAVISTA', 'OLMEDO', 'OLMEDO', 'MANABI', 0.50, 100, '1318'],
  ['PAJAN', 'PAJAN', 'PAJAN', 'MANABI', 0.50, 100, '1310'],
  ['CASCOL', 'CASCOL', 'PAJAN', 'MANABI', 0.50, 100, '1310'],
  ['PEDRO PABLO GOMEZ', 'PEDRO PABLO GOMEZ', 'JIPIJAPA', 'MANABI', 0.50, 100, '1306'],
  ['SALANGO', 'SALANGO', 'PUERTO LOPEZ', 'MANABI', 0.50, 100, '1319'],
  ['RIO CHICO', 'SALANGO', 'PUERTO LOPEZ', 'MANABI', 0.50, 100, '1319'],
  ['PUERTO LOPEZ', 'PUERTO LOPEZ', 'PUERTO LOPEZ', 'MANABI', 0.50, 100, '1319'],
  ['AYAMPE', 'SALANGO', 'PUERTO LOPEZ', 'MANABI', 0.50, 100, '1319'],
  ['MONTECRISTI', 'MONTECRISTI', 'MONTECRISTI', 'MANABI', 0.50, 100, '1309'],
  ['AGUA PATO', 'EL ANEGADO (CAB EN ELOY ALFARO)', 'JIPIJAPA', 'MANABI', 0.50, 100, '1306'],
  ['COJIMIES', 'COJIMIES', 'PEDERNALES', 'MANABI', 0.50, 100, '1317'],
  ['PEDERNALES', 'PEDERNALES', 'PEDERNALES', 'MANABI', 0.50, 101, '1317'],
  ['QUIROGA', 'QUIROGA', 'BOLIVAR', 'MANABI', 0.50, 101, '1302'],
  ['JUNIN', 'JUNIN', 'JUNIN', 'MANABI', 0.50, 101, '1307'],
  ['CANUTO', 'CANUTO', 'CHONE', 'MANABI', 0.50, 101, '1303'],
  ['TOSAGUA', 'TOSAGUA', 'TOSAGUA', 'MANABI', 0.50, 101, '1315'],
  ['EL PUEBLITO', 'CHARAPOTO', 'SUCRE', 'MANABI', 0.50, 101, '1314'],
  ['JAMA', 'JAMA', 'JAMA', 'MANABI', 0.50, 101, '1320'],
  ['MANTA', 'MONTECRISTI', 'MONTECRISTI', 'MANABI', 0.50, 101, '1309'],
  ['SAN CLEMENTE', 'CHARAPOTO', 'SUCRE', 'MANABI', 0.50, 101, '1314'],
  ['SAN PABLO', 'SAN PABLO (PUEBLO NUEVO)', 'SANTA ANA', 'MANABI', 0.40, 101, '1313'],
  ['EL CARMEN', 'EL CARMEN', 'EL CARMEN', 'MANABI', 0.40, 101, '1304'],
  ['LA BRAMADORA', 'EL CARMEN', 'EL CARMEN', 'MANABI', 0.40, 101, '1304'],
  ['BARRAGANETE', 'BARRAGANETE', 'PICHINCHA', 'MANABI', 0.40, 101, '1311'],
  ['MEMBRILLO', 'MEMBRILLO', 'BOLIVAR', 'MANABI', 0.40, 101, '1302'],
  ['OLMEDO', 'OLMEDO', 'OLMEDO', 'MANABI', 0.40, 101, '1318'],
  ['PICHINCHA', 'PICHINCHA', 'PICHINCHA', 'MANABI', 0.40, 101, '1311'],
  ['LA UNION', 'LA UNION', 'SANTA ANA', 'MANABI', 0.40, 101, '1313'],
  ['LASCANO', 'LASCANO', 'PAJAN', 'MANABI', 0.40, 101, '1310'],
  ['GUALE', 'GUALE', 'PAJAN', 'MANABI', 0.40, 101, '1310'],
  ['BAHIA DE CARAQUEZ', 'BAHIA DE CARAQUEZ', 'SUCRE', 'MANABI', 0.50, 101, '1314'],
  ['MACHALILLA', 'MACHALILLA', 'PUERTO LOPEZ', 'MANABI', 0.50, 101, '1319'],
  ['PUERTO DE CAYO', 'PUERTO DE CAYO', 'JIPIJAPA', 'MANABI', 0.50, 101, '1306'],
  ['PUERTO RICO', 'SALANGO', 'PUERTO LOPEZ', 'MANABI', 0.50, 101, '1319'],
  ['PALORA', 'PALORA (METZERA)', 'PALORA', 'MORONA SANTIAGO', 0.30, 101, '1404'],
  ['PABLO SEXTO', 'PABLO SEXTO', 'PABLO SEXTO', 'MORONA SANTIAGO', 0.30, 101, '1411'],
  ['GENERAL LEONIDAS PLAZA GUTIERREZ', 'GRAL. LEONIDAS PLAZA GUTIERREZ', 'LIMON INDANZA', 'MORONA SANTIAGO', 0.30, 101, '1403'],
  ['SANTIAGO DE MENDEZ', 'SANTIAGO DE MENDEZ', 'SANTIAGO', 'MORONA SANTIAGO', 0.30, 101, '1405'],
  ['SUC┌A', 'SUCUA', 'SUCUA', 'MORONA SANTIAGO', 0.30, 101, '1406'],
  ['MACAS', 'MACAS', 'MORONA', 'MORONA SANTIAGO', 0.30, 101, '1401'],
  ['CUYUJA', 'CUYUJA', 'QUIJOS', 'NAPO', 0.40, 101, '1507'],
  ['SAN FRANCISCO DE BORJA', 'SAN FRANCISCO DE BORJA', 'QUIJOS', 'NAPO', 0.40, 102, '1507'],
  ['EL CHACO', 'EL CHACO', 'EL CHACO', 'NAPO', 0.40, 102, '1504'],
  ['BAEZA', 'BAEZA', 'QUIJOS', 'NAPO', 0.40, 102, '1507'],
  ['PAPALLACTA', 'PAPALLACTA', 'QUIJOS', 'NAPO', 0.40, 102, '1507'],
  ['ARCHIDONA', 'ARCHIDONA', 'ARCHIDONA', 'NAPO', 0.35, 102, '1503'],
  ['COTUNDO', 'COTUNDO', 'ARCHIDONA', 'NAPO', 0.35, 102, '1503'],
  ['TENA', 'TENA', 'TENA', 'NAPO', 0.35, 102, '1501'],
  ['LA JOYA DE LOS SACHAS', 'LA JOYA DE LOS SACHAS', 'LA JOYA DE LOS SACHAS', 'ORELLANA', 0.15, 102, '2203'],
  ['TARACOA', 'TARACOA', 'ORELLANA', 'ORELLANA', 0.15, 102, '2201'],
  ['NUEVO ROCAFUERTE', 'NUEVO ROCAFUERTE', 'AGUARICO', 'ORELLANA', 0.15, 102, '2202'],
  ['TIPUTINI', 'TIPUTINI', 'AGUARICO', 'ORELLANA', 0.15, 102, '2202'],
  ['PAYAMINO', 'PUERTO FRANCISCO DE ORELLANA', 'ORELLANA', 'ORELLANA', 0.25, 102, '2201'],
  ['SAN SEBASTIAN DEL COCA', 'SAN SEBASTIAN DEL COCA', 'LA JOYA DE LOS SACHAS', 'ORELLANA', 0.25, 102, '2203'],
  ['PUERTO FRANCISCO DE ORELLANA', 'PUERTO FRANCISCO DE ORELLANA', 'ORELLANA', 'ORELLANA', 0.25, 102, '2201'],
  ['SHELL', 'SHELL', 'MERA', 'PASTAZA', 0.30, 102, '1602'],
  ['PUYO', 'PUYO', 'PASTAZA', 'PASTAZA', 0.30, 102, '1601'],
  ['MERA', 'MERA', 'MERA', 'PASTAZA', 0.35, 102, '1602'],
  ['SIMËN BOLIVAR', 'PUERTO QUITO', 'PUERTO QUITO', 'PICHINCHA', 0.50, 102, '1709'],
  ['BUENOS AIRES', 'PUERTO QUITO', 'PUERTO QUITO', 'PICHINCHA', 0.50, 102, '1709'],
  ['EL QUINCHE', 'EL QUINCHE', 'QUITO', 'PICHINCHA', 0.40, 102, '1701'],
  ['SAN ANTONIO DE PICHINCHA', 'SAN ANTONIO', 'QUITO', 'PICHINCHA', 0.40, 102, '1701'],
  ['CUMBAYA', 'CUMBAYA', 'QUITO', 'PICHINCHA', 0.40, 102, '1701'],
  ['ARMENIA', 'GUANGOPOLO', 'QUITO', 'PICHINCHA', 0.40, 102, '1701'],
  ['UYUMBICHO', 'AMAGUAÑA', 'QUITO', 'PICHINCHA', 0.40, 102, '1701'],
  ['ALOAG', 'ALOAG', 'MEJIA', 'PICHINCHA', 0.40, 102, '1703'],
  ['CANGAGHUA', 'CANGAHUA', 'CAYAMBE', 'PICHINCHA', 0.40, 102, '1702'],
  ['GUAYLLABAMBA', 'GUAYLLABAMBA', 'QUITO', 'PICHINCHA', 0.40, 102, '1701'],
  ['ASCAZUBI', 'ASCAZUBI', 'CAYAMBE', 'PICHINCHA', 0.40, 102, '1702'],
  ['POMASQUI', 'POMASQUI', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['CALDERON', 'CALDERON (CARAPUNGO)', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['NAYON', 'NAYON', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['ZAMBIZA', 'ZAMBIZA', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['YARUQUI', 'YARUQUI', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['CHECA', 'CHECA (CHILPA)', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['ALOAG', 'MACHACHI', 'MEJIA', 'PICHINCHA', 0.40, 103, '1703'],
  ['AMAGUAÑA', 'AMAGUAÑA', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['PINTAG', 'PINTAG', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['LA MERCED', 'LA MERCED', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['PUEMBO', 'PUEMBO', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['TABABELA', 'TABABELA', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['LLANO GRANDE', 'CALDERON (CARAPUNGO)', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['MACHACHI', 'MACHACHI', 'MEJIA', 'PICHINCHA', 0.40, 103, '1703'],
  ['ANGUMBA', 'TABACUNDO', 'PEDRO MONCAYO', 'PICHINCHA', 0.40, 103, '1704'],
  ['HUAYCUPATA', 'CAYAMBE', 'CAYAMBE', 'PICHINCHA', 0.40, 103, '1702'],
  ['NANEGAL', 'NANEGAL', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['GUALEA', 'GUALEA', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['TUPIGACHI', 'TUPIGACHI', 'PEDRO MONCAYO', 'PICHINCHA', 0.40, 103, '1704'],
  ['CAYAMBE', 'CAYAMBE', 'CAYAMBE', 'PICHINCHA', 0.40, 103, '1702'],
  ['JUAN MONTALVO', 'CAYAMBE', 'CAYAMBE', 'PICHINCHA', 0.40, 103, '1702'],
  ['TABACUNDO', 'TABACUNDO', 'PEDRO MONCAYO', 'PICHINCHA', 0.40, 103, '1704'],
  ['LA ESPERANZA', 'LA ESPERANZA', 'PEDRO MONCAYO', 'PICHINCHA', 0.40, 103, '1704'],
  ['GUARAQUI', 'LA ESPERANZA', 'PEDRO MONCAYO', 'PICHINCHA', 0.40, 103, '1704'],
  ['TOCACHI', 'TOCACHI', 'PEDRO MONCAYO', 'PICHINCHA', 0.40, 103, '1704'],
  ['MALCHINGUI', 'MALCHINGUI', 'PEDRO MONCAYO', 'PICHINCHA', 0.40, 103, '1704'],
  ['PERUCHO', 'PERUCHO', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['PUELLARO', 'PUELLARO', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['ALCHIPICHI', 'PUELLARO', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['PULULAHUA', 'CALACALI', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['CASPIGASI DEL CARMEN', 'SAN ANTONIO', 'QUITO', 'PICHINCHA', 0.40, 103, '1701'],
  ['CALACALI', 'CALACALI', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['OLMEDO', 'OLMEDO (PECILLO)', 'CAYAMBE', 'PICHINCHA', 0.40, 104, '1702'],
  ['ATAHUALPA', 'ATAHUALPA (HABASPAMBA)', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['SAN JOSE DE MINAS', 'SAN JOSE DE MINAS', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['SAN MIGUEL DE LOS BANCOS', 'SAN MIGUEL DE LOS BANCOS', 'SAN MIGUEL DE LOS BANCOS', 'PICHINCHA', 0.40, 104, '1707'],
  ['NANEGALITO', 'NANEGALITO', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['AYORA', 'CAYAMBE', 'CAYAMBE', 'PICHINCHA', 0.40, 104, '1702'],
  ['SANGOLQUI', 'SANGOLQUI', 'RUMIÑAHUI', 'PICHINCHA', 0.40, 104, '1705'],
  ['PUERTO QUITO', 'PUERTO QUITO', 'PUERTO QUITO', 'PICHINCHA', 0.40, 104, '1709'],
  ['PEDRO VICENTE MALDONADO', 'PEDRO VICENTE MALDONADO', 'PEDRO VICENTE MALDONADO', 'PICHINCHA', 0.40, 104, '1708'],
  ['TUMBACO', 'TUMBACO', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['PIFO', 'PIFO', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['TAMBILLO', 'TAMBILLO', 'MEJIA', 'PICHINCHA', 0.40, 104, '1703'],
  ['SANTA ROSA', 'CONOCOTO', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['SAN RAFAEL', 'AMAGUAÑA', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['CONOCOTO', 'CONOCOTO', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['QUITO', 'POMASQUI', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['ALANGASI', 'ALANGASI', 'QUITO', 'PICHINCHA', 0.40, 104, '1701'],
  ['SAN PABLO', 'SANTA ELENA', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['ATAHUALPA', 'ATAHUALPA', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['SANTA ELENA', 'SANTA ELENA', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['SAN JOSE', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['LA CURIA', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['OLON', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['MONTAÑITA', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['EL MAMEY', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['FEBRES CORDERO', 'COLONCHE', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['BAMBIL CALLAO', 'COLONCHE', 'SANTA ELENA', 'SANTA ELENA', 0.50, 104, '2401'],
  ['BARCELONA', 'COLONCHE', 'SANTA ELENA', 'SANTA ELENA', 0.50, 105, '2401'],
  ['SAN PEDRO DE VALDIVIA', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 105, '2401'],
  ['AYANGUE', 'COLONCHE', 'SANTA ELENA', 'SANTA ELENA', 0.50, 105, '2401'],
  ['SINCAL', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 105, '2401'],
  ['MANGLARALTO', 'MANGLARALTO', 'SANTA ELENA', 'SANTA ELENA', 0.50, 105, '2401'],
  ['LA LIBERTAD', 'SALINAS', 'SALINAS', 'SANTA ELENA', 0.50, 105, '2403'],
  ['SALINAS', 'SALINAS', 'SALINAS', 'SANTA ELENA', 0.50, 105, '2403'],
  ['SANTA MARÍA DEL TOACHI', 'SANTA MARIA DEL TOACHI', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.35, 105, '2301'],
  ['LUZ DE AMÉRICA', 'LUZ DE AMERICA', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['PUERTO LIMÓN', 'PUERTO LIMON', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['EL ESFUERZO', 'EL ESFUERZO', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['SAN JACINTO DE BUA', 'SAN JACINTO DEL BUA', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['VALLE HERMOSO', 'VALLE HERMOSO', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['CRISTÓBAL COLÓN', 'VALLE HERMOSO', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['NUEVO ISRAEL', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['LAS DELICIAS', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['LA AURORA', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['JULIO MORENO', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['UNIÓN CÍVICA POPULAR', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['SUSANITA', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['EULOGIO', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['SANTO DOMINGO DE LOS COLORADOS', 'STO. DOMINGO DE LOS COLORADOS', 'SANTO DOMINGO', 'STO. DOMINGO DE LOS TSACHILAS', 0.40, 105, '2301'],
  ['SHUSHUFINDI', 'SHUSHUFINDI', 'SHUSHUFINDI', 'SUCUMBIOS', 0.15, 105, '2104'],
  ['SIETE DE JULIO', 'SIETE DE JULIO', 'SHUSHUFINDI', 'SUCUMBIOS', 0.15, 105, '2104'],
  ['EL ENO', 'EL ENO', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['AGUARICO', 'PACAYACU', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['CHIRITZA', 'TARAPOA', 'CUYABENO', 'SUCUMBIOS', 0.15, 106, '2107'],
  ['SAN PEDRO DE LOS COFANES', 'SAN PEDRO DE LOS COFANES', 'SHUSHUFINDI', 'SUCUMBIOS', 0.15, 106, '2104'],
  ['JIVINO', 'SAN PEDRO DE LOS COFANES', 'SHUSHUFINDI', 'SUCUMBIOS', 0.15, 106, '2104'],
  ['PUERTO EL CARMEN DEL PUTUMAYO', 'PUERTO EL CARMEN DEL PUTUMAYO', 'PUTUMAYO', 'SUCUMBIOS', 0.15, 106, '2103'],
  ['PUERTO AGUARICO', 'NUEVA LOJA', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['SANTA CECILIA', 'SANTA CECILIA', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['GENERAL FARFAN', 'GENERAL FARFAN', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['SAN MIGUEL', 'GENERAL FARFAN', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['NUEVA LOJA', 'NUEVA LOJA', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['SANTA ROSA', 'NUEVA LOJA', 'LAGO AGRIO', 'SUCUMBIOS', 0.15, 106, '2101'],
  ['JAMBELI', 'JAMBELI', 'LAGO AGRIO', 'SUCUMBIOS', 0.25, 106, '2101'],
  ['SEVILLA', 'SEVILLA', 'CASCALES', 'SUCUMBIOS', 0.25, 106, '2106'],
  ['EL DORADO DE CASCALES', 'EL DORADO DE CASCALES', 'CASCALES', 'SUCUMBIOS', 0.30, 106, '2106'],
  ['EL DORADO DE CASCALES', 'LUMBAQUI', 'GONZALO PIZARRO', 'SUCUMBIOS', 0.30, 106, '2102'],
  ['COMUNA TAMBO LOMA', 'PILAGUIN (PILAHUIN)', 'AMBATO', 'TUNGURAHUA', 0.35, 106, '1801'],
  ['CORAZON DE JESUS', 'IZAMBA', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['URBANIZACION AEROPUERTO', 'IZAMBA', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['PILAHUIN', 'PILAGUIN (PILAHUIN)', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['QUINSAPINCHA', 'QUISAPINCHA (QUIZAPINCHA)', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['JUAN BENIGNO VELA', 'JUAN BENIGNO VELA', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['MARTINEZ', 'AUGUSTO N. MARTINEZ', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['SAN ISIDRO', 'PICAIGUA', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['TOTORAS', 'TOTORAS', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['AMBATILLO', 'AMBATILLO', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['ATAHUALPA', 'ATAHUALPA (CHISALATA)', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['IZAMBA', 'IZAMBA', 'AMBATO', 'TUNGURAHUA', 0.40, 106, '1801'],
  ['SAN MIGUELITO', 'SAN MIGUELITO', 'SANTIAGO DE PILLARO', 'TUNGURAHUA', 0.40, 107, '1808'],
  ['HUACHI GRANDE', 'HUACHI GRANDE', 'AMBATO', 'TUNGURAHUA', 0.40, 107, '1801'],
  ['SANTA ROSA', 'SANTA ROSA', 'AMBATO', 'TUNGURAHUA', 0.40, 107, '1801'],
  ['AMBATO', 'AMBATO', 'AMBATO', 'TUNGURAHUA', 0.40, 107, '1801'],
  ['PELILEO GRANDE', 'PELILEO', 'SAN PEDRO DE PELILEO', 'TUNGURAHUA', 0.40, 107, '1807'],
  ['SAN ANTONIO DE PASA', 'PASA', 'AMBATO', 'TUNGURAHUA', 0.40, 107, '1801'],
  ['SAN BARTOLOME DE PINLLOG', 'SAN BARTOLOME DE PINLLOG', 'AMBATO', 'TUNGURAHUA', 0.40, 107, '1801'],
  ['PATATE', 'PATATE', 'PATATE', 'TUNGURAHUA', 0.40, 107, '1805'],
  ['SAN FERNANDO', 'SAN FERNANDO', 'AMBATO', 'TUNGURAHUA', 0.40, 107, '1801'],
  ['EMILIO M. TERAN', 'EMILIO MARIA TERAN (RUMIPAMBA)', 'SANTIAGO DE PILLARO', 'TUNGURAHUA', 0.40, 107, '1808'],
  ['MARCOS ESPINEL', 'MARCOS ESPINEL (CHACATA)', 'SANTIAGO DE PILLARO', 'TUNGURAHUA', 0.40, 107, '1808'],
  ['BAÑOS DE AGUA SANTA', 'BAÑOS DE AGUA SANTA', 'BAÑOS DE AGUA SANTA', 'TUNGURAHUA', 0.40, 107, '1802'],
  ['ULBA', 'ULBA', 'BAÑOS DE AGUA SANTA', 'TUNGURAHUA', 0.40, 107, '1802'],
  ['RIO VERDE', 'RIO VERDE', 'BAÑOS DE AGUA SANTA', 'TUNGURAHUA', 0.40, 107, '1802'],
  ['GUAMBALÓ', 'GUAMBALO (HUAMBALO)', 'SAN PEDRO DE PELILEO', 'TUNGURAHUA', 0.40, 107, '1807'],
  ['TISALEO', 'TISALEO', 'TISALEO', 'TUNGURAHUA', 0.40, 107, '1809'],
  ['CEVALLOS', 'CEVALLOS', 'CEVALLOS', 'TUNGURAHUA', 0.40, 107, '1803'],
  ['QUERO', 'QUERO', 'QUERO', 'TUNGURAHUA', 0.40, 107, '1806'],
  ['RUMIPAMBA', 'RUMIPAMBA', 'QUERO', 'TUNGURAHUA', 0.40, 107, '1806'],
  ['COTALÓ', 'COTALO', 'SAN PEDRO DE PELILEO', 'TUNGURAHUA', 0.40, 107, '1807'],
  ['SAN ANDRÉS', 'SAN ANDRES', 'SANTIAGO DE PILLARO', 'TUNGURAHUA', 0.40, 107, '1808'],
  ['CUNCHIBAMBA', 'CUNCHIBAMBA', 'AMBATO', 'TUNGURAHUA', 0.40, 107, '1801'],
  ['SAN JOSÉ DE POALÓ', 'SAN JOSE DE POALO', 'SANTIAGO DE PILLARO', 'TUNGURAHUA', 0.40, 107, '1808'],
  ['EL TRIUNFO', 'EL TRIUNFO', 'PATATE', 'TUNGURAHUA', 0.40, 107, '1805'],
  ['SUCRE', 'LOS ANDES (CAB EN POATUG)', 'PATATE', 'TUNGURAHUA', 0.40, 107, '1805'],
  ['MOCHA', 'MOCHA', 'MOCHA', 'TUNGURAHUA', 0.40, 107, '1804'],
  ['PELILEO', 'PELILEO', 'SAN PEDRO DE PELILEO', 'TUNGURAHUA', 0.40, 108, '1807'],
  ['PILLARO', 'PILLARO', 'SANTIAGO DE PILLARO', 'TUNGURAHUA', 0.40, 108, '1808'],
  ['GUAYZIMI', 'GUAYZIMI', 'NANGARITZA', 'ZAMORA CHINCHIPE', 0.30, 108, '1903'],
  ['YANTZAZA', 'YANTZAZA (YANZATZA)', 'YANTZAZA', 'ZAMORA CHINCHIPE', 0.30, 108, '1905'],
  ['ZUMBA', 'ZUMBA', 'CHINCHIPE', 'ZAMORA CHINCHIPE', 0.25, 108, '1902'],
  ['ZAMORA', 'ZAMORA', 'ZAMORA', 'ZAMORA CHINCHIPE', 0.25, 108, '1901'],
  ['LAS GOLONDRINAS', 'LAS GOLONDRINAS', 'LAS GOLONDRINAS', 'ZONA NO DELIMITADA', 0.50, 108, null],
  ['SANTA MARÍA', 'MANGA DEL CURA', 'MANGA DEL CURA', 'ZONA NO DELIMITADA', 0.40, 108, null],
  ['SANTA TERESA', 'MANGA DEL CURA', 'MANGA DEL CURA', 'ZONA NO DELIMITADA', 0.40, 108, null],
];

export const POBLACIONES_NEC: PoblacionNEC[] = DATOS.map(([poblacion, parroquia, canton, provincia, z, pagina, codigoInec]) => ({
  poblacion, parroquia, canton, provincia, z, zona: zonaPorZ(z), pagina, codigoInec,
}));

export interface ZonaDeCanton { zona: ZonaSismica; z: number; poblacion: PoblacionNEC; poblaciones: number; traza: Traza }

// Poblaciones del cantón en la Tabla 19, de la Z mayor a la menor (a igual Z, en el orden de la tabla).
export function poblacionesPorCanton(codigoInec: string | null | undefined): PoblacionNEC[] {
  if (!codigoInec) return [];
  return POBLACIONES_NEC.filter(p => p.codigoInec === codigoInec).sort((a, b) => b.z - a.z);
}

// Nombre que se guarda en emergency_plans.seismic_place: la población; si el nombre se repite en el
// cantón (p. ej. ALOAG en las parroquias Aloag y Machachi), se agrega la parroquia.
export function etiquetaPoblacion(p: PoblacionNEC): string {
  const mismas = POBLACIONES_NEC.filter(x => x.codigoInec === p.codigoInec && x.poblacion === p.poblacion);
  return mismas.length > 1 ? `${p.poblacion} (${p.parroquia})` : p.poblacion;
}

export function poblacionPorEtiqueta(codigoInec: string | null | undefined, etiqueta: string | null | undefined): PoblacionNEC | undefined {
  if (!etiqueta) return undefined;
  return poblacionesPorCanton(codigoInec).find(p => etiquetaPoblacion(p) === etiqueta);
}

const zComa = (z: number) => String(z).replace('.', ',');

// Zona de una población elegida, con su traza "NEC-SE-DS Tabla 19, población X, Z z".
export function zonaDePoblacion(p: PoblacionNEC): ZonaDeCanton {
  const del = poblacionesPorCanton(p.codigoInec);
  return {
    zona: p.zona, z: p.z, poblacion: p, poblaciones: del.length,
    traza: {
      metodo: 'amenaza_vulnerabilidad', norma: 'NEC-SE-DS', articulo_o_tabla: 'Tabla 19 (§10.2) y Tabla 1', umbral: null,
      valor: `Z = ${zComa(p.z)} → zona ${p.zona}`, unidad: 'g', estado: 'cumple',
      fuente_doc: `${FUENTE_TABLA19}, pág. ${p.pagina}`,
      nota: `NEC-SE-DS Tabla 19, población ${etiquetaPoblacion(p)}, Z ${zComa(p.z)}`,
    },
  };
}

// Zona MÁS EXIGENTE (Z mayor) de las poblaciones del cantón: es la que se propone por defecto. null si el
// cantón no aparece en la Tabla 19: entonces la zona se elige a mano (seismic_zone_source = 'manual').
export function zonaPorCanton(codigoInec: string | null | undefined): ZonaDeCanton | null {
  const del = poblacionesPorCanton(codigoInec);
  return del.length ? zonaDePoblacion(del[0]) : null;
}
